import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faHourglassHalf,
  faPauseCircle,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useState } from "react";
import axiosInstance from "../utils/axios-instance";
import GetUser from "../utils/get-user";
import UseGraph from "../utils/use-graph";
import formQueryUrl from "../utils/form-query-url";

type WidgetItem = {
  status: string;
  label: string;
  icon: IconDefinition;
  iconClass: string;
  ringClass: string;
  barClass: string;
};

const widgetData: WidgetItem[] = [
  {
    status: "Awaiting-Customer",
    label: "Awaiting",
    icon: faHourglassHalf,
    iconClass: "text-amber-500",
    ringClass: "bg-amber-500/10",
    barClass: "bg-amber-500",
  },
  {
    status: "On-Hold",
    label: "On Hold",
    icon: faPauseCircle,
    iconClass: "text-indigo-500",
    ringClass: "bg-indigo-500/10",
    barClass: "bg-indigo-500",
  },

  {
    status: "Collected",
    label: "Collected",
    icon: faCircleCheck,
    iconClass: "text-emerald-500",
    ringClass: "bg-emerald-500/10",
    barClass: "bg-emerald-500",
  },
  {
    status: "Failed",
    label: "Failed",
    icon: faCircleXmark,
    iconClass: "text-orange-500",
    ringClass: "bg-orange-500/10",
    barClass: "bg-orange-500",
  },
  {
    status: "Pending-Cancellation",
    label: "Pending",
    icon: faTriangleExclamation,
    iconClass: "text-pink-500",
    ringClass: "bg-pink-500/10",
    barClass: "bg-pink-500",
  },
  {
    status: "Cancelled",
    label: "Cancelled",
    icon: faBan,
    iconClass: "text-rose-500",
    ringClass: "bg-rose-500/10",
    barClass: "bg-rose-500",
  },
];

export default function TopBarWidget() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate, isRestarted } = UseGraph();

  useEffect(() => {
    const findWidgets = async () => {
      setLoading(true);
      const filtersArray = [
        isRestarted && { restarted: true },
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const widgetData = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`,
        );
        setWidgetCount(widgetData.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    findWidgets();
  }, [user?.code, isMyCustomer, startDate, endDate, isRestarted]);

  const totalCount = widgetCount.reduce(
    (sum, widgetC) => sum + (Number(widgetC?.count) || 0),
    0,
  );

  return (
    <div className="mt-7 grid grid-cols-3 gap-4 smallTablet:grid-cols-2">
      {widgetData.map((item) => {
        const currentWidegtDetails = widgetCount.find(
          (widgetC) => widgetC.status === item.status,
        );

        if (loading) {
          return (
            <div
              className="animate-pulse w-[100%] h-[104px] bg-surface border border-edge rounded-2xl"
              key={item.status}
            ></div>
          );
        }

        const count = Number(currentWidegtDetails?.count) || 0;
        const share = totalCount > 0 ? (count / totalCount) * 100 : 0;

        return (
          <div
            className="flex flex-col bg-surface border border-edge w-[100%] rounded-2xl shadow-card hover:border-edge-strong transition-colors duration-200 px-5 py-4 gap-4"
            key={item.status}
          >
            <div className="flex gap-3.5 items-center">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${item.ringClass}`}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={`text-[17px] ${item.iconClass}`}
                />
              </div>
              <div className="flex flex-col min-w-0 gap-1">
                <p className="font-medium leading-none text-[13px] text-light-gray text-nowrap !capitalize">
                  {item.label}
                </p>

                <p className="text-heading-color text-[26px] font-bold leading-none tracking-tight tabular-nums">
                  {count}
                </p>
              </div>
              <p className="text-[12px] font-semibold text-light-gray tabular-nums ml-auto shrink-0 self-start">
                {Math.round(share)}%
              </p>
            </div>

            <div className="h-1.5 w-full rounded-full bg-overlay-mid overflow-hidden mt-auto">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ${item.barClass}`}
                style={{ width: `${Math.min(share, 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
